const LEGACY_DB_NAME = 'gengage_request_response_cache';

interface RequestResponseCacheOptions {
  /** @deprecated Browser response caching is disabled. Retained for API compatibility. */
  ttlMs?: number;
  /** @deprecated Browser response caching is disabled. Retained for API compatibility. */
  maxEntries?: number;
  /** @deprecated Browser response caching is disabled. Retained for API compatibility. */
  maxBodyBytes?: number;
}

let legacyCleanup: Promise<void> | null = null;

/**
 * Compatibility transport for callers that historically used the FE response cache.
 *
 * Dynamic assistant, product, search, Q&A, and similarity responses are always fetched
 * from their owner. Browser HTTP caching is disabled too, so freshness is governed by
 * the site/backend contract rather than a second, invisible client-side response copy.
 */
export async function cachedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  _options: RequestResponseCacheOptions = {},
): Promise<Response> {
  void purgeLegacyRequestResponseCache();
  return fetch(input, { ...init, cache: 'no-store' });
}

/**
 * Kept for source compatibility with integrations that used the helper for diagnostics.
 * Returning null is the explicit contract that no browser response cache key exists.
 */
export function requestCacheKey(_input: RequestInfo | URL, _init: RequestInit = {}): null {
  return null;
}

export async function clearRequestResponseCacheForTests(): Promise<void> {
  if (legacyCleanup) await legacyCleanup;
  legacyCleanup = null;
  await purgeLegacyRequestResponseCache();
}

async function purgeLegacyRequestResponseCache(): Promise<void> {
  if (typeof indexedDB === 'undefined' || indexedDB === null || typeof indexedDB.deleteDatabase !== 'function') {
    return;
  }
  if (legacyCleanup) return legacyCleanup;
  legacyCleanup = deleteDatabase(LEGACY_DB_NAME);
  return legacyCleanup;
}

async function deleteDatabase(name: string): Promise<void> {
  if (typeof indexedDB === 'undefined' || indexedDB === null || typeof indexedDB.deleteDatabase !== 'function') {
    return;
  }
  await new Promise<void>((resolve) => {
    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      request.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

// Module evaluation is the unconditional SDK startup path shared by legacy fetch,
// attachment, and injector-adapter transports. Cleanup never gates a live request.
void purgeLegacyRequestResponseCache();
